import { ThemeEvents } from '@theme/events';

/** Packs are presentation/selection only. Shopify owns discounts and the native form owns transport. */
export function validPackConfig(config, product, currency) {
  if (config?.schema_version !== 1 || config.enabled !== true || config.currency !== currency) return false;
  if (!Number.isSafeInteger(config.unit_price_cents) || config.unit_price_cents <= 0) return false;
  if (!product?.variants?.length || !product.options?.length) return false;
  if (product.variants.some((v) => v.price !== config.unit_price_cents || v.min !== 1 || v.increment !== 1)) return false;
  if (!Array.isArray(config.tiers) || config.tiers.length !== 3) return false;
  return [1, 2, 3].every((quantity) => {
    const matches = config.tiers.filter((tier) => tier.quantity === quantity);
    if (matches.length !== 1) return false;
    const total = matches[0].total_cents;
    return Number.isSafeInteger(total) && total > 0 && total <= quantity * config.unit_price_cents &&
      (quantity !== 1 || total === config.unit_price_cents);
  });
}

/** Exact option tuples only; never replace a missing/unavailable choice with another variant. */
export function resolvePackSelection(product, choices) {
  const counts = new Map();
  for (let index = 0; index < choices.length; index += 1) {
    const choice = choices[index];
    const matches = product.variants.filter((v) => choice?.length === product.options.length &&
      v.options.every((value, optionIndex) => value === choice[optionIndex]));
    if (matches.length !== 1 || !matches[0].available) return { errorIndex: index, errorCode: 'unavailable' };
    const variant = matches[0];
    const quantity = (counts.get(variant.id)?.quantity || 0) + 1;
    if (variant.max != null && quantity > variant.max) return { errorIndex: index, errorCode: 'quantity' };
    counts.set(variant.id, { variantId: variant.id, quantity });
  }
  return { items: [...counts.values()], quantity: choices.length };
}

class SofthourPackPicker extends HTMLElement {
  connectedCallback() {
    if (this.lifecycle) return;
    this.lifecycle = new AbortController();
    const { signal } = this.lifecycle;
    try {
      this.config = JSON.parse(this.querySelector('[data-pack-config]').textContent);
      this.product = JSON.parse(this.querySelector('[data-pack-product]').textContent);
    } catch { return; }
    if (!validPackConfig(this.config, this.product, this.dataset.currency)) return;
    this.scope = this.closest('.shopify-section');
    this.quantity = 1;
    this.addEventListener('change', this.onChange, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate, { signal });
    document.addEventListener('shopify:section:load', this.onSectionLoad, { signal });
    // A module can upgrade before the later buy-buttons block is parsed.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.bind(), { once: true, signal });
    }
    queueMicrotask(() => this.bind());
  }

  disconnectedCallback() {
    this.lifecycle?.abort();
    this.lifecycle = undefined;
    this.formObserver?.disconnect();
    this.form?.removeAttribute('data-softhour-packs');
    this.restoreSticky();
    // The section is normally removed with the picker; restore if the editor removes only this block.
    const sourceLabel = this.form?.querySelector('.add-to-cart-text__content');
    if (sourceLabel && this.originalLabel) sourceLabel.textContent = this.originalLabel;
    this.ready = false;
  }

  nativePicker() {
    return this.scope?.querySelector(`variant-picker[data-product-id="${CSS.escape(this.dataset.productId)}"]`);
  }

  primaryChoices() {
    const groups = Array.from(this.nativePicker()?.querySelectorAll('.variant-option') || []);
    return this.product.options.map((option, index) => {
      const group = groups[index];
      return group?.querySelector('input:checked')?.value || group?.querySelector('select')?.value ||
        (option.values.length === 1 ? option.values[0] : null);
    });
  }

  bind() {
    if (!this.isConnected) return;
    const htmlForm = document.getElementById(this.dataset.formId);
    const form = htmlForm?.closest('product-form-component');
    if (!form || form.dataset.productId !== this.dataset.productId || !this.scope?.contains(form)) return;
    if (!this.nativePicker() && this.product.options.some((option) => option.values.length > 1)) return;
    if (this.form !== form) {
      this.formObserver?.disconnect();
      this.form = form;
      this.originalLabel = form.querySelector('.add-to-cart-text__content')?.textContent?.trim();
      this.formObserver = new MutationObserver(() => this.paintButton());
      this.formObserver.observe(form, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    }
    if (!this.ready) {
      const primary = this.primaryChoices();
      this.querySelectorAll('[data-pack-pair]').forEach((row) => {
        row.querySelectorAll('[data-pack-option]').forEach((group) => {
          group.querySelectorAll('input').forEach((input) => { input.checked = input.value === primary[Number(group.dataset.packOption)]; });
        });
      });
    }
    this.ready = true;
    this.hidden = false;
    this.form.setAttribute('data-softhour-packs', this.id);
    const quantityInput = htmlForm.querySelector('[name="quantity"]');
    if (quantityInput) quantityInput.value = '1';
    this.querySelectorAll('[data-pack-quantity]').forEach((input) => { input.disabled = false; });
    this.paint();
  }

  onSectionLoad = (event) => {
    if (event.target.contains(this)) queueMicrotask(() => this.bind());
  };

  onVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    // Both native form and sticky morph during this event; derive the display afterwards.
    queueMicrotask(() => this.bind());
  };

  onChange = (event) => {
    if (event.target.matches('[data-pack-quantity]')) this.quantity = Number(event.target.value);
    this.paint();
  };

  tier() { return this.config.tiers.find((tier) => tier.quantity === this.quantity); }
  card() { return this.querySelector(`[data-pack-card="${this.quantity}"]`); }
  label() { return this.card()?.querySelector('.sf-packs__label')?.textContent?.trim() || ''; }
  formattedTotal() { return this.card()?.querySelector('.sf-packs__total')?.textContent?.trim() || ''; }

  rowChoices(row) {
    return this.product.options.map((_, index) => row.querySelector(`[data-pack-option="${index}"] input:checked`)?.value || null);
  }

  paint() {
    if (!this.ready) return;
    this.querySelector('[data-pack-first]').hidden = this.quantity === 1;
    this.querySelectorAll('[data-pack-pair]').forEach((row) => {
      row.hidden = Number(row.dataset.packPair) > this.quantity;
      const choices = this.rowChoices(row);
      row.querySelectorAll('[data-pack-option]').forEach((group) => {
        const optionIndex = Number(group.dataset.packOption);
        group.querySelector('[data-pack-value]').textContent = choices[optionIndex] ? `: ${choices[optionIndex]}` : '';
        group.querySelectorAll('input').forEach((input) => {
          input.disabled = row.hidden;
          const available = this.product.variants.some((variant) => variant.available &&
            variant.options.every((value, index) => value === (index === optionIndex ? input.value : choices[index])));
          input.setAttribute('aria-disabled', String(!available));
        });
      });
    });
    this.paintButton();
    this.dispatchEvent(new CustomEvent('sf:pack-change', { bubbles: true, detail: {
      productId: this.dataset.productId, quantity: this.quantity, totalCents: this.tier().total_cents,
    } }));
  }

  sticky() {
    return this.scope?.querySelector(`sticky-add-to-cart[data-product-id="${CSS.escape(this.dataset.productId)}"]`);
  }

  restoreSticky() {
    const sticky = this.sticky();
    sticky?.querySelectorAll('[data-sf-pack-summary]').forEach((summary) => summary.remove());
    const price = sticky?.querySelector('.sticky-add-to-cart__price');
    if (price) price.hidden = false;
  }

  paintButton() {
    if (!this.ready) return;
    const source = this.form?.querySelector('[ref="addToCartButton"]');
    if (!source || source.disabled) return;
    const label = `${this.dataset.buttonLabel || 'Add to bag'} · ${this.label()} · ${this.formattedTotal()}`;
    const content = source.querySelector('.add-to-cart-text__content');
    if (content && content.textContent !== label) content.textContent = label;
    const sticky = this.sticky();
    const stickyLabel = sticky?.querySelector('.add-to-cart-text__content');
    if (stickyLabel && stickyLabel.textContent !== label) stickyLabel.textContent = label;
    const stickyButton = sticky?.querySelector('[ref="addToCartButton"]');
    if (stickyButton) stickyButton.setAttribute('aria-label', label);
    const nativePrice = sticky?.querySelector('.sticky-add-to-cart__price');
    if (nativePrice) {
      nativePrice.hidden = true;
      let summary = sticky.querySelector('[data-sf-pack-summary]');
      if (!summary) {
        summary = document.createElement('span');
        summary.className = 'sf-packs__sticky-total';
        summary.dataset.sfPackSummary = '';
        nativePrice.insertAdjacentElement('afterend', summary);
      }
      summary.textContent = `${this.label()} · ${this.formattedTotal()}`;
    }
  }

  getSubmission() {
    if (!this.ready || this.quantity === 1) return null;
    const rows = [...this.querySelectorAll('[data-pack-pair]')].filter((row) => Number(row.dataset.packPair) <= this.quantity);
    const result = resolvePackSelection(this.product, [this.primaryChoices(), ...rows.map((row) => this.rowChoices(row))]);
    if (result.errorCode) {
      const pair = result.errorIndex + 1;
      const target = pair === 1 ? this.nativePicker() : rows[result.errorIndex - 1];
      return {
        error: result.errorCode === 'quantity'
          ? `The quantity selected for pair ${pair} exceeds the limit for that size and color.`
          : `Choose an available color and size for pair ${pair}.`,
        focusTarget: target?.querySelector('input:checked, select, input'),
      };
    }
    return result;
  }
}

if (!customElements.get('softhour-pack-picker')) customElements.define('softhour-pack-picker', SofthourPackPicker);
